import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  FaCalendarAlt,
  FaSearch,
  FaStore,
  FaUser,
  FaFileExcel,
  FaChartLine,
  FaCashRegister,
  FaClock
} from "react-icons/fa";
import { BsGraphUpArrow } from "react-icons/bs";
import { FiTrendingUp, FiDollarSign, FiShoppingBag, FiCheck, FiFilter } from "react-icons/fi";
import { Line, Bar } from "react-chartjs-2";
import { motion, AnimatePresence } from "framer-motion";
import "./SalesReportPrint.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { format } from "date-fns";
import Swal from "sweetalert2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const SalesReport = () => {
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const printRef = useRef();

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const idOutletManager = localStorage.getItem("id_outlet");
  const [printMode, setPrintMode] = useState(null);

  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    cashier: "",
    outlet: "",
    name: "",
  });

  const [tempFilters, setTempFilters] = useState(filters);
  const [salesPerDay, setSalesPerDay] = useState([]);
  const [chartFilter, setChartFilter] = useState({
    outlet_id: "",
    start_date: "",
    end_date: "",
  });

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const [monthlyTransactions, setMonthlyTransactions] = useState([]);
  const [monthlyMeta, setMonthlyMeta] = useState(null);
  const [monthlyPage, setMonthlyPage] = useState(1);
  const [monthlyFilters, setMonthlyFilters] = useState({
    outlet_id: "",
    search: "",
    order_by: "date",
    sort: "DESC",
  });

  const [shiftOptions, setShiftOptions] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedShift, setSelectedShift] = useState("1");
  const [customStartTime, setCustomStartTime] = useState("08:00:00");
  const [customEndTime, setCustomEndTime] = useState("17:00:00");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const startDate = dateRange[0];
  const endDate = dateRange[1];
  const [salesPerCashier, setSalesPerCashier] = useState([]);
  const [selectedCashierId, setSelectedCashierId] = useState("");
  const [selectedCashierDate, setSelectedCashierDate] = useState("");

  // Fetch shifts
  useEffect(() => {
    const fetchShifts = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/v1/shift`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const validShifts = (res.data.data || []).filter((s) => s.code !== "L");
        setShiftOptions(validShifts);
      } catch (err) {
        console.error("Failed to fetch shifts:", err);
      }
    };
    fetchShifts();
  }, [token]);

  useEffect(() => {
    if (printMode === "grafik" || printMode === "transaksi" || printMode === "perjam") {
      const timeout = setTimeout(() => {
        window.print();
        setPrintMode(null);
      }, 600);
      return () => clearTimeout(timeout);
    }
  }, [printMode]);

  useEffect(() => {
    fetchCashiers();
    fetchOutlets();
  }, []);

  useEffect(() => {
    if (filters.start_date && filters.end_date) {
      fetchSalesReport();
    }
  }, [filters]);

  const exportTransactionsToExcel = async () => {
    try {
      let allData = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/v1/report/sales-report-per-month`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              outlet_id: monthlyFilters.outlet_id,
              search: monthlyFilters.search,
              order: monthlyFilters.order_by,
              sort: monthlyFilters.sort,
              per_page: 10,
              page: currentPage,
            },
          }
        );

        allData = [...allData, ...(res.data.data || [])];
        totalPages = res.data.meta?.last_page || 1;
        currentPage++;
      } while (currentPage <= totalPages);

      const worksheetData = allData.map((item) => ({
        Tanggal: new Date(item.date).toLocaleDateString("id-ID"),
        Kode: item.code,
        Status: item.status,
        Jenis: item.type,
        Jumlah: item.amount,
      }));

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Transaksi Bulanan");

      const outletName = monthlyFilters.outlet_id
        ? outlets.find((o) => o.value === monthlyFilters.outlet_id)?.label || "Semua_Outlet"
        : "Semua_Outlet";

      const bulanTahun =
        allData.length > 0
          ? new Date(allData[0].date).toLocaleDateString("id-ID", { month: "long", year: "numeric" })
          : "Semua_Waktu";

      const searchText = monthlyFilters.search
        ? `_Cari_${monthlyFilters.search.replace(/\s+/g, "_")}`
        : "";

      const fileName = `Laporan_Transaksi_Bulanan_${outletName}_${bulanTahun}${searchText}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error("Gagal export semua data:", err);
      Swal.fire({
        icon: "error",
        title: "Gagal Export Data",
        text: "Terjadi kesalahan saat mengunduh data Excel.",
        customClass: { popup: "rounded-3xl" },
      });
    }
  };

  const exportChartAndDataToExcel = async () => {
    const chartCanvas = document.getElementById("weeklyChart");
    if (!chartCanvas) {
      Swal.fire({
        icon: "warning",
        title: "Grafik Tidak Ditemukan",
        text: "Data grafik mingguan belum tersedia untuk diexport.",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    const chartImage = chartCanvas.toDataURL("image/png");
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Grafik Mingguan");

    const imageId = workbook.addImage({
      base64: chartImage,
      extension: "png",
    });

    worksheet.addImage(imageId, {
      tl: { col: 1, row: 1 },
      ext: { width: 500, height: 300 },
    });

    worksheet.addRow([]);
    worksheet.addRow(["Hari", "Penjualan Minggu Ini", "Penjualan Minggu Lalu"]);

    chartData.forEach((item) => {
      worksheet.addRow([item.day, item.this_week, item.last_week]);
    });

    const outletName = filters.outlet
      ? outlets.find((o) => o.value === filters.outlet)?.label || "Semua_Outlet"
      : "Semua_Outlet";

    const startDateStr = filters.start_date || "Semua_Tanggal";
    const endDateStr = filters.end_date || startDateStr;

    const fileName = `Grafik_Mingguan_${outletName}_${startDateStr}_${endDateStr}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(blob, fileName);
  };

  const exportSalesPerDayToExcel = async () => {
    const chartCanvas = document.querySelector("#salesPerDayChart canvas");
    if (!chartCanvas) {
      Swal.fire({
        icon: "warning",
        title: "Grafik Tidak Ditemukan",
        text: "Grafik Penjualan per Hari belum tersedia.",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    const chartImage = chartCanvas.toDataURL("image/png");
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Penjualan Per Hari");

    const imageId = workbook.addImage({
      base64: chartImage,
      extension: "png",
    });
    worksheet.addImage(imageId, {
      tl: { col: 1, row: 1 },
      ext: { width: 500, height: 300 },
    });

    worksheet.addRow([]);
    worksheet.addRow(["No", "Nama Kasir", "Total Penjualan"]);
    salesPerDay.forEach((item, idx) => {
      worksheet.addRow([idx + 1, item.fullname, item.total_amount]);
    });

    const outletName = chartFilter.outlet_id
      ? outlets.find((o) => o.value === chartFilter.outlet_id)?.label || "Semua_Outlet"
      : "Semua_Outlet";
    const startDateStr = chartFilter.start_date
      ? chartFilter.start_date.split("T")[0]
      : "Semua_Tanggal";
    const endDateStr = chartFilter.end_date
      ? chartFilter.end_date.split("T")[0]
      : startDateStr;

    const fileName = `Grafik_Penjualan_Per_Hari_${outletName}_${startDateStr}_${endDateStr}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName);
  };

  const exportSalesPerCashierToExcel = async () => {
    const chartCanvas = document.querySelector("#salesPerCashierChart canvas");
    if (!chartCanvas) {
      Swal.fire({
        icon: "warning",
        title: "Grafik Tidak Ditemukan",
        text: "Grafik Penjualan per Jam belum tersedia.",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    const chartImage = chartCanvas.toDataURL("image/png");
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Penjualan Per Jam");

    const imageId = workbook.addImage({
      base64: chartImage,
      extension: "png",
    });
    worksheet.addImage(imageId, {
      tl: { col: 1, row: 1 },
      ext: { width: 500, height: 300 },
    });

    worksheet.addRow([]);
    worksheet.addRow(["No", "Jam", "Jumlah Penjualan"]);
    salesPerCashier.forEach((item, idx) => {
      worksheet.addRow([idx + 1, item.hour, item.amount]);
    });

    const outletName = tempFilters.outlet
      ? outlets.find((o) => o.value === tempFilters.outlet)?.label || "Semua_Outlet"
      : "Semua_Outlet";
    const dateStr = selectedCashierDate || "Semua_Tanggal";
    const cashierName = selectedCashierId
      ? cashiers.find((c) => c.value === selectedCashierId)?.label || "Semua_Kasir"
      : "Semua_Kasir";

    const fileName = `Grafik_Penjualan_Per_Jam_${cashierName}_${outletName}_${dateStr}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName);
  };

  const fetchCashiers = async (outletIdFromSelect = null) => {
    try {
      let outletId = outletIdFromSelect;
      if (role === "manager") {
        outletId = localStorage.getItem("id_outlet");
      }

      if (!outletId) return;

      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/v1/employee`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          role: "",
          id_outlet: outletId,
        },
      });

      setCashiers(
        (res.data.data || []).map((emp) => ({
          value: String(emp.id),
          label: emp.full_name,
        }))
      );
    } catch (err) {
      console.error("Failed to fetch cashiers", err);
    }
  };

  const fetchOutlets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/v1/outlet`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOutlets(
        (res.data.data || []).map((outlet) => ({
          value: String(outlet.id),
          label: outlet.name,
        }))
      );
    } catch (err) {
      console.error("Failed to fetch outlets", err);
    }
  };

  const fetchSalesReport = async (customFilters = filters) => {
    try {
      const params = {
        page: 1,
        per_page: 1,
        order_by: "t.created_at",
        sort: "DESC",
      };

      if (customFilters.start_date) params.start_date = customFilters.start_date;
      if (customFilters.end_date) params.end_date = customFilters.end_date;
      if (customFilters.outlet) params.outlet = customFilters.outlet;
      if (customFilters.cashier) params.cashier = customFilters.cashier;
      if (customFilters.name) params.name = customFilters.name;

      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/v1/report/sales-report`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const data = res.data.data;
      setSummary(data.summary);
      setChartData(data.chart || []);
      setTransactions(data.transactions || []);
    } catch (err) {
      console.error("Failed to fetch sales report", err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilter = () => {
    setFilters(tempFilters);
    fetchSalesReport(tempFilters);
  };

  const lineData = {
    labels: chartData.map((item) => item.day),
    datasets: [
      {
        label: "Minggu Ini",
        data: chartData.map((item) => item.this_week),
        borderColor: "#059669",
        backgroundColor: "rgba(5, 150, 105, 0.1)",
        tension: 0.3,
        fill: true,
      },
      {
        label: "Minggu Lalu",
        data: chartData.map((item) => item.last_week),
        borderColor: "#94a3b8",
        backgroundColor: "rgba(148, 163, 184, 0.1)",
        tension: 0.3,
        fill: true,
      },
    ],
  };

  const salesPerDayBarData = {
    labels: salesPerDay.map((item) => item.fullname),
    datasets: [
      {
        label: "Total Penjualan",
        data: salesPerDay.map((item) => item.total_amount),
        backgroundColor: "rgba(13, 148, 136, 0.7)",
        borderColor: "#0d9488",
        borderWidth: 1,
        borderRadius: 8,
        barThickness: 28,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `Rp ${value.toLocaleString("id-ID")}`,
        },
      },
    },
  };

  const fetchSalesPerDay = async (filter) => {
    try {
      if (!filter.outlet_id || !filter.start_date || !filter.end_date) return;

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/report/sales-report-per-day`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            outlet_id: Number(filter.outlet_id),
            start_date: filter.start_date,
            end_date: filter.end_date,
          },
        }
      );
      setSalesPerDay(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch sales per day", err);
    }
  };

  useEffect(() => {
    if (role !== "manager") {
      if (tempFilters.outlet) {
        fetchCashiers(tempFilters.outlet);
      } else {
        setCashiers([]);
      }
    } else {
      fetchCashiers();
    }
  }, [tempFilters.outlet, role]);

  const fetchMonthlyTransactions = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/report/sales-report-per-month`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            outlet_id: monthlyFilters.outlet_id,
            page: monthlyPage,
            search: monthlyFilters.search,
            per_page: 10,
            order: monthlyFilters.order_by,
            sort: monthlyFilters.sort,
          },
        }
      );

      setMonthlyTransactions(res.data.data || []);
      setMonthlyMeta(res.data.meta);
    } catch (err) {
      console.error("Failed to fetch monthly transactions", err);
    }
  };

  useEffect(() => {
    if (monthlyFilters.outlet_id) {
      fetchMonthlyTransactions();
    }
  }, [monthlyFilters, monthlyPage]);

  const fetchSalesPerCashier = async (cashierId, date) => {
    if (!cashierId || !date) {
      setSalesPerCashier([]);
      return;
    }
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/report/sales-report-per-cashier/${cashierId}?date=${date}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSalesPerCashier(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch sales per cashier", err);
    }
  };

  useEffect(() => {
    if (role === "manager" && idOutletManager) {
      setTempFilters((prev) => ({ ...prev, outlet: idOutletManager }));
      setFilters((prev) => ({ ...prev, outlet: idOutletManager }));
      setMonthlyFilters((prev) => ({ ...prev, outlet_id: idOutletManager }));
      setChartFilter((prev) => ({ ...prev, outlet_id: idOutletManager }));
    }
  }, [role, idOutletManager]);

  return (
    <div className="space-y-6 pb-12">
      {/* Header Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-50 rounded-2xl border border-emerald-100 text-emerald-600">
            <BsGraphUpArrow className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Laporan Penjualan (Sales Report)
            </h1>
            <p className="text-xs text-gray-500">
              Analisis pendapatan, grafik transaksi mingguan, harian, dan per jam kasir
            </p>
          </div>
        </div>

        {/* Global Export Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={exportChartAndDataToExcel}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-emerald-600/20 transition-all"
        >
          <FaFileExcel className="w-4 h-4" />
          <span>Export Grafik & Data ke Excel</span>
        </motion.button>
      </motion.div>

      {/* Filter Panel */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-4 text-xs"
      >
        <h3 className="text-xs font-extrabold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
          <FiFilter className="text-emerald-600 w-4 h-4" />
          <span>Filter Laporan Utama</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="space-y-1">
            <span className="text-gray-400 font-bold uppercase text-[10px]">Tanggal Mulai</span>
            <input
              type="date"
              name="start_date"
              value={tempFilters.start_date}
              onChange={handleFilterChange}
              className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            />
          </div>

          <div className="space-y-1">
            <span className="text-gray-400 font-bold uppercase text-[10px]">Tanggal Sampai</span>
            <input
              type="date"
              name="end_date"
              value={tempFilters.end_date}
              onChange={handleFilterChange}
              className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            />
          </div>

          <div className="space-y-1">
            <span className="text-gray-400 font-bold uppercase text-[10px]">Nama Item</span>
            <input
              type="text"
              name="name"
              placeholder="Filter nama obat..."
              value={tempFilters.name}
              onChange={handleFilterChange}
              className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            />
          </div>

          <div className="space-y-1">
            <span className="text-gray-400 font-bold uppercase text-[10px]">Outlet Cabang</span>
            <select
              name="outlet"
              value={tempFilters.outlet}
              onChange={handleFilterChange}
              disabled={role === "manager"}
              className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            >
              <option value="">Semua Outlet</option>
              {outlets.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <span className="text-gray-400 font-bold uppercase text-[10px]">Kasir</span>
            <select
              name="cashier"
              value={tempFilters.cashier}
              onChange={handleFilterChange}
              className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            >
              <option value="">Semua Kasir</option>
              {cashiers.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleApplyFilter}
            className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-1.5"
          >
            <FiCheck className="w-4 h-4" />
            <span>Terapkan Filter</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Metric Summary Cards */}
      {summary && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5"
        >
          <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
                Total Penjualan Omset
              </p>
              <h2 className="text-2xl font-extrabold text-emerald-700 font-mono">
                Rp {(summary.total_sales || 0).toLocaleString("id-ID")}
              </h2>
            </div>
            <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
              <FiDollarSign className="w-6 h-6" />
            </div>
          </div>

          <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
                Jumlah Transaksi
              </p>
              <h2 className="text-3xl font-extrabold text-teal-600 font-mono">
                {summary.transaction_amount || 0}
              </h2>
            </div>
            <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
              <FaCashRegister className="w-6 h-6" />
            </div>
          </div>

          <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
                Total Item Terjual
              </p>
              <h2 className="text-3xl font-extrabold text-amber-600 font-mono">
                {summary.item_sales || 0}
              </h2>
            </div>
            <div className="p-3.5 rounded-2xl bg-amber-50 text-amber-600 border border-amber-100">
              <FiShoppingBag className="w-6 h-6" />
            </div>
          </div>
        </motion.div>
      )}

      {/* 1. Grafik Mingguan Line Chart */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-4"
      >
        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
          <h2 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
            <FaChartLine className="text-emerald-600 w-4 h-4" />
            <span>Statistik Pendapatan Mingguan</span>
          </h2>
        </div>

        {chartData.length > 0 ? (
          <div className="p-4 bg-gray-50/50 rounded-2xl border border-gray-100">
            <Line id="weeklyChart" data={lineData} />
          </div>
        ) : (
          <div className="p-8 text-center text-gray-400 font-semibold text-xs">
            Belum ada data untuk grafik mingguan.
          </div>
        )}
      </motion.div>

      {/* 2. Tabel Transaksi Bulanan */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-4"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pb-3 border-b border-gray-100">
          <h2 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
            <FaFileExcel className="text-emerald-600 w-4 h-4" />
            <span>Tabel Transaksi Bulanan</span>
          </h2>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={exportTransactionsToExcel}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-md shadow-emerald-600/20 transition-all"
          >
            <FaFileExcel className="w-3.5 h-3.5" />
            <span>Export Transaksi ke Excel</span>
          </motion.button>
        </div>

        {/* Filter Monthly Table */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div className="relative">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Cari kode transaksi..."
              value={monthlyFilters.search}
              onChange={(e) => setMonthlyFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            />
          </div>

          <select
            value={monthlyFilters.outlet_id}
            onChange={(e) => {
              if (role !== "manager") {
                setMonthlyFilters((prev) => ({ ...prev, outlet_id: e.target.value }));
              }
            }}
            disabled={role === "manager"}
            className="p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
          >
            <option value="">Semua Outlet</option>
            {role === "manager"
              ? outlets
                  .filter((o) => o.value === localStorage.getItem("id_outlet"))
                  .map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))
              : outlets.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
          </select>
        </div>

        <div ref={printRef} className={`${printMode === "transaksi" ? "print-area" : ""}`}>
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm text-xs">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5">Tanggal</th>
                  <th className="p-3.5">Kode Transaksi</th>
                  <th className="p-3.5">Status</th>
                  <th className="p-3.5">Jenis Pembayaran</th>
                  <th className="p-3.5 text-right">Jumlah Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {monthlyTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="p-8 text-center text-gray-400 font-semibold">
                      Belum ada transaksi bulanan ditemukan
                    </td>
                  </tr>
                ) : (
                  monthlyTransactions.map((item, index) => (
                    <tr key={index} className="hover:bg-emerald-50/20 transition-colors">
                      <td className="p-3.5 font-mono font-semibold text-gray-700">
                        {new Date(item.date).toLocaleDateString("id-ID")}
                      </td>
                      <td className="p-3.5 font-mono font-extrabold text-gray-900">{item.code}</td>
                      <td className="p-3.5">
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {item.status || "Paid"}
                        </span>
                      </td>
                      <td className="p-3.5 font-semibold text-gray-700">{item.type || "Cash"}</td>
                      <td className="p-3.5 text-right font-mono font-extrabold text-emerald-700">
                        Rp {(item.amount || 0).toLocaleString("id-ID")}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {monthlyMeta && (
          <div className="flex justify-between items-center pt-3 border-t border-gray-100 text-xs">
            <p className="text-gray-400">
              Halaman {monthlyMeta.current_page} dari {monthlyMeta.last_page}
            </p>
            <div className="flex gap-2">
              <button
                disabled={monthlyPage === 1}
                onClick={() => setMonthlyPage((prev) => Math.max(1, prev - 1))}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold disabled:opacity-40 transition-colors"
              >
                Sebelumnya
              </button>
              <button
                disabled={monthlyPage === monthlyMeta.last_page}
                onClick={() => setMonthlyPage((prev) => Math.min(monthlyMeta.last_page, prev + 1))}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold disabled:opacity-40 transition-colors"
              >
                Berikutnya
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* 3. Grafik Total Penjualan Per Hari */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.5 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-4"
      >
        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
          <h2 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
            <BsGraphUpArrow className="text-emerald-600 w-4 h-4" />
            <span>Grafik Total Penjualan Per Hari</span>
          </h2>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={exportSalesPerDayToExcel}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-md shadow-emerald-600/20 transition-all"
          >
            <FaFileExcel className="w-3.5 h-3.5" />
            <span>Export Grafik Kasir ke Excel</span>
          </motion.button>
        </div>

        {/* Filter Controls for Per Day Chart */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="space-y-1">
            <span className="text-gray-400 font-bold uppercase text-[10px]">Outlet Cabang</span>
            <select
              value={chartFilter.outlet_id}
              onChange={(e) => {
                if (role !== "manager") setChartFilter((prev) => ({ ...prev, outlet_id: e.target.value }));
              }}
              disabled={role === "manager"}
              className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            >
              <option value="">Pilih Outlet</option>
              {role === "manager"
                ? outlets
                    .filter((o) => o.value === localStorage.getItem("id_outlet"))
                    .map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))
                : outlets.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
            </select>
          </div>

          <div className="space-y-1 relative md:col-span-2">
            <span className="text-gray-400 font-bold uppercase text-[10px]">Tanggal & Shift</span>
            <button
              type="button"
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
              className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold text-left focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            >
              {startDate && endDate
                ? `${format(startDate, "yyyy-MM-dd")} ${customStartTime} → ${format(endDate, "yyyy-MM-dd")} ${customEndTime}`
                : startDate
                ? `${format(startDate, "yyyy-MM-dd")} ${customStartTime} → ${format(startDate, "yyyy-MM-dd")} ${customEndTime}`
                : "Klik Untuk Pilih Tanggal & Shift..."}
            </button>

            {isDatePickerOpen && (
              <div className="absolute left-0 z-50 mt-2 p-3 bg-white border border-gray-200 rounded-3xl shadow-2xl space-y-3 w-80">
                <DatePicker
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  onSelect={(date) => {
                    if (selectedShift !== "custom") {
                      const shift = shiftOptions.find((s) => String(s.id) === selectedShift);
                      if (shift) {
                        setCustomStartTime(shift.start_time);
                        setCustomEndTime(shift.end_time);
                      }
                      setDateRange([date, date]);
                    }
                  }}
                  onChange={(update) => {
                    if (selectedShift === "custom") {
                      const [start, end] = update;
                      setDateRange([start, end]);
                    }
                  }}
                  inline
                />

                <div className="space-y-2 border-t border-gray-100 pt-2">
                  <div className="space-y-1">
                    {shiftOptions.map((shift) => (
                      <button
                        key={shift.id}
                        type="button"
                        onClick={() => {
                          setSelectedShift(shift.id.toString());
                          setDateRange([null, null]);
                          setCustomStartTime(shift.start_time);
                          setCustomEndTime(shift.end_time);
                        }}
                        className={`w-full text-left p-2 rounded-xl text-xs font-semibold transition-colors ${
                          selectedShift === String(shift.id)
                            ? "bg-emerald-50 text-emerald-700 font-bold"
                            : "hover:bg-gray-50 text-gray-700"
                        }`}
                      >
                        {shift.name} ({shift.start_time} - {shift.end_time})
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (!startDate) {
                        Swal.fire({
                          icon: "warning",
                          title: "Pilih Tanggal",
                          text: "Pilih tanggal terlebih dahulu pada kalender!",
                          customClass: { popup: "rounded-3xl" },
                        });
                        return;
                      }

                      const fromDate = format(startDate, "yyyy-MM-dd");
                      const fromTime = customStartTime;
                      const toDate = format(
                        selectedShift === "custom" ? endDate || startDate : startDate,
                        "yyyy-MM-dd"
                      );
                      const toTime = customEndTime;

                      const from = `${fromDate}T${fromTime}`;
                      const to = `${toDate}T${toTime}`;

                      const updatedChartFilter = {
                        outlet_id: chartFilter.outlet_id,
                        start_date: from,
                        end_date: to,
                      };

                      setChartFilter(updatedChartFilter);
                      fetchSalesPerDay(updatedChartFilter);
                      setIsDatePickerOpen(false);
                    }}
                    className="w-full py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold text-xs shadow-md shadow-emerald-600/20"
                  >
                    Terapkan Shift & Tanggal
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Chart Render */}
        <div ref={printRef} className={`p-4 bg-gray-50/50 rounded-2xl border border-gray-100 text-xs ${printMode === "grafik" ? "print-area" : ""}`}>
          {salesPerDay.length > 0 ? (
            <div id="salesPerDayChart">
              <Bar data={salesPerDayBarData} options={barOptions} />
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400 font-semibold">
              Belum ada data penjualan per hari untuk filter ini.
            </div>
          )}
        </div>
      </motion.div>

      {/* 4. Grafik Penjualan Per Jam Per Kasir */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.6 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-4"
      >
        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
          <h2 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
            <FaClock className="text-emerald-600 w-4 h-4" />
            <span>Grafik Penjualan Per Jam (Per Kasir)</span>
          </h2>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={exportSalesPerCashierToExcel}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-md shadow-emerald-600/20 transition-all"
          >
            <FaFileExcel className="w-3.5 h-3.5" />
            <span>Export Penjualan per Jam ke Excel</span>
          </motion.button>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="space-y-1">
            <span className="text-gray-400 font-bold uppercase text-[10px]">Pilih Outlet</span>
            <select
              value={tempFilters.outlet}
              disabled={role === "manager"}
              onChange={(e) => {
                const outletId = e.target.value;
                setTempFilters((prev) => ({ ...prev, outlet: outletId }));
                setSelectedCashierId("");
                fetchCashiers(outletId);
              }}
              className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            >
              <option value="">Pilih Outlet</option>
              {outlets.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <span className="text-gray-400 font-bold uppercase text-[10px]">Pilih Kasir</span>
            <select
              value={selectedCashierId}
              onChange={(e) => {
                const cashierId = e.target.value;
                setSelectedCashierId(cashierId);
                if (cashierId && selectedCashierDate) fetchSalesPerCashier(cashierId, selectedCashierDate);
              }}
              className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            >
              <option value="">Pilih Kasir</option>
              {cashiers.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <span className="text-gray-400 font-bold uppercase text-[10px]">Pilih Tanggal</span>
            <input
              type="date"
              value={selectedCashierDate}
              onChange={(e) => {
                const date = e.target.value;
                setSelectedCashierDate(date);
                if (selectedCashierId && date) fetchSalesPerCashier(selectedCashierId, date);
              }}
              className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            />
          </div>
        </div>

        {/* Chart Render */}
        <div className="p-4 bg-gray-50/50 rounded-2xl border border-gray-100 text-xs">
          {salesPerCashier.length > 0 ? (
            <div id="salesPerCashierChart">
              <Line
                data={{
                  labels: salesPerCashier.map((item) => item.hour),
                  datasets: [
                    {
                      label: "Penjualan per Jam",
                      data: salesPerCashier.map((item) => item.amount),
                      fill: true,
                      borderColor: "#0d9488",
                      backgroundColor: "rgba(13, 148, 136, 0.1)",
                      tension: 0.3,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "top" }, title: { display: false } },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: (value) => `Rp ${value.toLocaleString("id-ID")}`,
                      },
                    },
                  },
                }}
              />
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400 font-semibold">
              Pilih kasir dan tanggal untuk melihat grafik penjualan per jam.
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default SalesReport;